/*
  Vloeiende kleurwissel op de productpagina.

  WAAROM
  Symmetry haalt bij elke kleurklik de hele productsectie opnieuw van de server
  en vervangt daarna de HTML. Dat kost ongeveer twee seconden en de pagina lijkt
  te verspringen: de galerij wordt opnieuw opgebouwd, de miniaturen beginnen van
  voren af aan en het thema zet de focus terug op het verborgen keuzerondje,
  wat op een telefoon een blijvende rand om de swatch geeft.

  Dat ophalen is niet nodig. De galerij van het thema kan zelf al lokaal van
  kleurgroep wisselen: media-gallery luistert op 'on:variant:change' en verbergt
  of toont de juiste foto's. Alleen prijs en beschikbaarheid kwamen uit de
  opgehaalde HTML.

  HOE
  We onderscheppen de wissel vóór het thema (capture-fase) en handelen hem
  lokaal af zodra dat veilig kan. Kan het niet veilig, dan laten we het thema
  gewoon zijn eigen werk doen; dan is het net zo traag als voorheen maar wel
  correct. Veilig betekent: de nieuwe variant is bekend én kost hetzelfde als de
  huidige, want de prijsopmaak (valuta, kortingsbadges) laten we liever met rust.

  De volledige variantlijst halen we één keer op via /products/<handle>.js.
*/
(() => {
  'use strict';

  const cache = new Map();

  async function laadVarianten(picker) {
    const pad = (picker.dataset.url || window.location.pathname).split('?')[0];
    if (cache.has(pad)) return cache.get(pad);
    /* De belofte staat tijdelijk in de cache zodat er niet twee keer wordt
       opgehaald; zodra hij klaar is vervangen we hem door de lijst zelf. */
    const belofte = fetch(`${pad}.js`)
      .then((r) => r.json())
      .then((p) => {
        cache.set(pad, p.variants);
        return p.variants;
      })
      .catch(() => {
        cache.delete(pad);
        return null;
      });
    cache.set(pad, belofte);
    return belofte;
  }

  function zoekVariant(varianten, opties) {
    return varianten.find((v) => opties.every((waarde, i) => waarde == null || v.options[i] === waarde));
  }

  function zelfdePrijs(a, b) {
    return a && b && a.price === b.price && a.compare_at_price === b.compare_at_price;
  }

  /* Per optie markeren welke waarden bij de nieuwe keuze uitverkocht zijn.
     Normaal komt dat uit de opgehaalde HTML; hier rekenen we het zelf uit. */
  function werkBeschikbaarheidBij(picker, varianten, opties) {
    const selectors = Array.from(picker.optionSelectors || picker.querySelectorAll('.option-selector'));
    selectors.forEach((selector, index) => {
      selector.querySelectorAll('.js-option').forEach((optie) => {
        const waarde = optie.dataset.value != null ? optie.dataset.value : optie.value;
        if (waarde == null || waarde === '') return;
        const proef = opties.slice();
        proef[index] = waarde;
        const kanDit = varianten.some((v) => v.available && proef.every((w, i) => w == null || v.options[i] === w));
        optie.classList.toggle('is-unavailable', !kanDit);
      });
    });
  }

  function koppel(picker) {
    if (picker.dataset.vloeiendeWissel) return;
    picker.dataset.vloeiendeWissel = '1';

    laadVarianten(picker);

    picker.addEventListener('change', (evt) => {
      const varianten = cache.get((picker.dataset.url || window.location.pathname).split('?')[0]);
      /* Nog niet geladen of een belofte die nog loopt: laat het thema het doen. */
      if (!varianten || typeof varianten.then === 'function') return;

      let opties;
      let huidige;
      try {
        opties = picker.getSelectedOptions();
        huidige = picker.getVariantData();
      } catch (e) {
        return;
      }
      if (!opties || opties.indexOf(null) !== -1) return;

      const doel = zoekVariant(varianten, opties);
      if (!doel || !zelfdePrijs(huidige, doel)) return; // prijs wijkt af: thema laten fetchen

      /* Vanaf hier doen we het zelf. Het thema komt er niet meer aan te pas,
         dus geen fetch, geen vervangen van de sectie en geen focussprong. */
      evt.stopPropagation();

      picker.variant = doel;
      const dataEl = picker.querySelector('[type="application/json"]');
      if (dataEl) dataEl.textContent = JSON.stringify(doel);

      const form = picker.getProductForm && picker.getProductForm();
      const idVeld = (form || document).querySelector('input[name="id"]');
      if (idVeld) idVeld.value = doel.id;

      werkBeschikbaarheidBij(picker, varianten, opties);

      try { picker.updateSwatchLabel(); } catch (e) { /* niet fataal */ }

      /* De galerij luistert hierop en wisselt zelf van kleurgroep en foto. */
      picker.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: { form, variant: doel, selectedOptions: opties }
      }));

      try { picker.updateUrl(); } catch (e) { /* niet fataal */ }
    }, true);
  }

  function start() {
    document.querySelectorAll('variant-picker').forEach(koppel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  /* Als het thema tóch een sectie vervangt, opnieuw koppelen. */
  document.addEventListener('on:variant:change', () => setTimeout(start, 50));
})();

/*
  Pijlknoppen van de carrousels.

  Het thema controleert in handleNavClick of het aangeklikte element zélf de
  knop is (evt.target.matches('.slider-nav__btn')). Klik je op het pijltje in
  die knop, dan is het doel de SVG en gebeurt er niets. Dat raakt elke
  carrousel van het thema, niet alleen de nieuwe.

  Hier zoeken we vanaf het aangeklikte element de knop op en voeren we dezelfde
  beweging uit die het thema zelf zou doen. Klikte je de knop wél direct aan,
  dan laten we het thema zijn gang gaan, anders zou hij twee slides opschuiven.
*/
(() => {
  'use strict';
  document.addEventListener('click', (evt) => {
    const doel = evt.target;
    if (!doel || typeof doel.closest !== 'function') return;
    const knop = doel.closest('.slider-nav__btn');
    if (!knop || doel === knop) return;

    const carrousel = knop.closest('carousel-slider');
    const slider = carrousel && carrousel.slider;
    if (!slider || !carrousel.slideSpan) return;

    const stap = (carrousel.slidesToScroll || 1) * carrousel.slideSpan;
    const richting = knop.name === 'next' ? 1 : -1;
    slider.scrollTo({ left: slider.scrollLeft + richting * stap, behavior: 'smooth' });
  });
})();

/* Upsell in de cart drawer. Toevoegen mag de winkelwagen niet verlaten, dus we
   vangen het formulier af en laten het thema daarna zelf verversen via het
   event waar de drawer al op luistert (on:cart:change). Gaat er iets mis, dan
   valt het formulier terug op de gewone POST naar /cart/add. */
document.addEventListener('submit', async (evt) => {
  const formulier = evt.target.closest('form[data-velo-upsell]');
  if (!formulier) return;
  evt.preventDefault();

  const knop = formulier.querySelector('.velo-upsell__add');
  if (knop.disabled) return;
  knop.disabled = true;

  try {
    const antwoord = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: formulier.querySelector('input[name="id"]').value,
        quantity: 1,
      }),
    });
    if (!antwoord.ok) throw new Error(antwoord.status);
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  } catch (fout) {
    formulier.submit();
  } finally {
    knop.disabled = false;
  }
});

/* De pijltjes schuiven de upsellrij één kaartbreedte op. */
document.addEventListener('click', (evt) => {
  const knop = evt.target.closest('[data-velo-upsell-nav]');
  if (!knop) return;
  const rij = knop.parentNode.querySelector('.velo-upsell__row');
  if (!rij) return;
  rij.scrollBy({ left: Number(knop.dataset.veloUpsellNav) * rij.clientWidth, behavior: 'smooth' });
});
